// src/pages/outsourcing_management/InquiryCreateForm.tsx
//
// Raising an inquiry - from an order, from a quote, or from nothing. The other
// half of InquiryDialog; InquiryEditForm is the half that changes one that
// already exists. Both render InquiryFields, so the inquiry asks for the same
// things in the same layout whichever way it is reached. Before this they were
// two separate modals: this form lived in components/Inquiry/InquiryComponent
// and drew its fields as a column of <Typography> elements used as containers.
//
// It renders through DialogColumns, the same three columns the saved inquiry
// gets: the inquiry and its item lines on the left, the email in the middle,
// and what can be done with it on the right - here, the buttons that raise it.
// Nothing moves when the new inquiry becomes a saved one. Raising one never
// emails the supplier; that is the saved inquiry's Send to supplier button.
//
// What is here and not in the edit half:
//   - the order link, which is a create-time choice (InquiryOrderPicker says
//     why),
//   - a live email preview, rebuilt from the form on every change. The saved
//     inquiry's email is previewed by id in InquiryDialog instead.
//
// The item lines are in both halves now: InquiryEditForm offers the same
// InquiryItemsTable in edit mode while the inquiry is still a Draft.
import {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Divider,
  LinearProgress,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import { apiCreateInquiry } from "util/network/Inquiries";
import { apiGetMe } from "util/network/Users";
import { getInitialDeadline } from "components/Inquiry/utils/inquiryPayload";
import { useEmailPreview } from "components/Inquiry/hooks/useEmailPreview";
import { useInquiryItems } from "components/Inquiry/hooks/useInquiryItems";
import EmailPreviewPanel from "components/Inquiry/EmailPreviewPanel";
import InquiryItemsTable from "components/Inquiry/items/InquiryItemsTable";
import { buildInquiryPayload } from "components/Inquiry/utils/inquiryPayload";
import { usePartners } from "hooks/usePartners";
import InquiryFields from "./InquiryFields";
import InquiryOrderPicker from "./InquiryOrderPicker";
import DialogColumns, { ItemLinesScroll } from "./InquiryDialogLayout";
import {
  InquiryFormValues,
  initialCreateValues,
} from "./inquiryFormValues";

type PropsType = {
  prodObjIn?: OrderType | CartType;
  prodType: "order" | "cart";
  onCancel: () => void;
  onCreated: (id: number) => void;
};

const InquiryCreateForm: FunctionComponent<PropsType> = ({
  prodObjIn,
  prodType,
  onCancel,
  onCreated,
}) => {
  const { token, employees } = useContext(AppStateContext);
  const { addToast } = useToast();
  const partners = usePartners() ?? [];

  const [values, setValues] = useState<InquiryFormValues>(() =>
    initialCreateValues(prodObjIn, prodType)
  );
  const [prodObj, setProdObj] = useState(prodObjIn);

  // The deadline is derived from the order, but the form is initialised
  // before one has necessarily been picked - initialCreateValues runs once,
  // from the prop. Picking an order afterwards has to back-fill it, or the
  // inquiry goes out with a deadline computed from nothing.
  //
  // Only when the user has not typed one: their own value outranks a
  // derived default.
  const [deadlineTouched, setDeadlineTouched] = useState(false);
  useEffect(() => {
    if (!prodObj || deadlineTouched) return;
    setValues((current) => ({
      ...current,
      deadline: getInitialDeadline(prodObj, prodType),
    }));
  }, [prodObj, prodType, deadlineTouched]);
  const [saving, setSaving] = useState(false);

  const patch = (changes: Partial<InquiryFormValues>) => {
    // A deadline the user typed outranks the one derived from the order.
    if ("deadline" in changes) setDeadlineTouched(true);
    setValues((current) => ({ ...current, ...changes }));
  };

  const items = useInquiryItems(prodObj, values.type, prodType);
  const partner = partners.find((p) => p.id === values.partnerId);

  const { html, isOpen, downloadPDF } = useEmailPreview({
    formValues: values,
    mergedItems: items.mergedItems,
    partner,
    prodObj,
    prodType,
    token,
  });

  // The inquiry is sent from whoever is raising it, so the reply reaches a
  // person rather than the shared inbox.
  useEffect(() => {
    apiGetMe(token)
      .then((me) =>
        patch({
          sendFromEmail:
            me.email || employees.items[0]?.email || "info@easypartz.com",
        })
      )
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCreate = () => {
    setSaving(true);
    apiCreateInquiry(
      token,
      buildInquiryPayload(values, items.mergedItems, prodObj, prodType),
      values.sendEmail,
      values.sendFromEmail
    )
      .then((created) => {
        addToast({ type: "success", message: "Inquiry created" });
        onCreated(created.id);
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `The inquiry could not be created: ${err}`,
          keep: true,
        })
      )
      .finally(() => setSaving(false));
  };

  return (
    <DialogColumns
      left={
        <>
          {saving && <LinearProgress sx={{ mb: 1 }} />}

          {prodObj ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {prodType === "order"
                ? `Order ${(prodObj as OrderType).orderNo}`
                : `Quote #${(prodObj as CartType).id}`}
            </Typography>
          ) : (
            <div style={{ marginBottom: "1.5rem" }}>
              <InquiryOrderPicker order={undefined} onPick={setProdObj} />
            </div>
          )}

          <InquiryFields
            values={values}
            onChange={patch}
            autoSelectSingleSupplier
            errors={
              values.partnerId
                ? {}
                : { partnerId: "An inquiry needs a supplier to go to" }
            }
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Item lines
          </Typography>
          <ItemLinesScroll>
            <InquiryItemsTable
              mode="edit"
              inquiryType={values.type}
              prodItems={items.prodItems}
              selectedItems={items.selectedItems}
              customItems={items.customItems}
              prodType={prodType}
              toggle={items.toggle}
              selectAll={items.selectAll}
              deselectAll={items.deselectAll}
              addCustomItem={items.addCustomItem}
              updateCustomItem={items.updateCustomItem}
              removeCustomItem={items.removeCustomItem}
            />
          </ItemLinesScroll>
        </>
      }
      middle={
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Email preview
          </Typography>
          <EmailPreviewPanel
            html={html}
            isOpen={isOpen}
            downloadPDF={downloadPDF}
          />
        </>
      }
      right={
        <>
          {/* Creating never emails - initialCreateValues keeps sendEmail
              false, so create_inquiry opens the inquiry at Draft. The email
              is sent by hand afterwards, from the inquiry's own Send to
              supplier button. */}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Saved as a draft - send it to the supplier from the inquiry itself.
          </Typography>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              margin: "0.5rem 0 1rem",
            }}
          >
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!values.partnerId || saving}
              sx={{
                background: "var(--mainYellow)",
                "&:hover": {
                  color: "var(--mainYellow)",
                  border: "1px solid var(--mainYellow)",
                  background: "transparent",
                },
              }}
            >
              {saving ? <CircularProgress size={18} /> : "Create inquiry"}
            </Button>
          </div>
        </>
      }
    />
  );
};

export default InquiryCreateForm;
